// backlog-review-support.ts — Logique PURE de la composition d'un support.
//
// L'écran R6 déplace des entrées, en retire, coche des options, et affiche en
// direct le plan et le nombre de slides. Rien de tout cela n'a besoin du DOM :
// tout est ici, testable, et la page ne fait que rendre le résultat.
//
// Trois règles de composition, et ce sont celles qu'une régression casserait
// sans qu'aucun test d'écran ne s'en aperçoive :
//
// 1. **« ⚑ À trancher » est alimentée par le drapeau, pas par l'utilisateur.**
//    On peut y retirer une entrée — elle sort du SUPPORT, le drapeau reste posé
//    dans la revue. On ne peut pas y ajouter : le drapeau se pose dans la revue.
// 2. **« Pour information » part vide** et n'accepte que de l'appliqué ou du
//    rejeté sans drapeau. C'est une sélection de composition, pas un état.
// 3. **Le plan compte comme le serveur rend.** Les deux arithmétiques doivent
//    coïncider, sinon l'écran annonce neuf slides et le fichier en contient
//    onze.

export type Section = 'arbitrate' | 'info';

export interface SupportReco {
  id: number;
  family: string;
  pile: string;
  to_arbitrate: boolean;
  title: string;
  targets: Array<{ type: string; id: number }>;
}

export interface SupportEntry {
  recommendation_id: number;
  section: Section;
  position: number;
}

// Doit rester égal à `support_pptx.DETAIL_MAX` côté serveur : au-delà, le rendu
// groupe par famille au lieu d'une slide par entrée.
export const DETAIL_MAX = 8;

export const INFO_PILES = ['applied', 'rejected'];

/** La sélection d'ouverture : les drapeautées, dans l'ordre de la revue. */
/** Les candidats d'une section, sélectionnés ou non.
 *
 * Les DEUX sections se remplissent seules et se vident à la main (décision
 * Christophe, 2026-08-26) : « À trancher » prend tout ce qui porte le drapeau,
 * « Pour information » tout ce qui a été appliqué ou rejeté sans drapeau. On
 * décoche ce qu'on ne veut pas présenter, on n'ajoute rien à la main — sinon il
 * y aurait deux endroits où décider ce qui passe en comité.
 */
export function candidatesFor(recos: SupportReco[], section: Section): SupportReco[] {
  if (section === 'arbitrate') return recos.filter((r) => r.to_arbitrate);
  return recos.filter((r) => !r.to_arbitrate && INFO_PILES.includes(r.pile));
}

/** La sélection de départ : TOUT, dans les deux sections. */
export function defaultSelection(recos: SupportReco[]): SupportEntry[] {
  const out: SupportEntry[] = [];
  for (const section of ['arbitrate', 'info'] as Section[]) {
    candidatesFor(recos, section).forEach((r, i) =>
      out.push({ recommendation_id: r.id, section, position: i + 1 }),
    );
  }
  return out;
}

/** Coche ou décoche toute une section d'un coup. */
export function setSectionSelected(
  entries: SupportEntry[],
  recos: SupportReco[],
  section: Section,
  selected: boolean,
): SupportEntry[] {
  const others = entries.filter((e) => e.section !== section);
  if (!selected) return renumber(others);
  const kept = entries.filter((e) => e.section === section);
  const known = new Set(kept.map((e) => e.recommendation_id));
  const added = candidatesFor(recos, section)
    .filter((r) => !known.has(r.id))
    .map((r, i) => ({ recommendation_id: r.id, section, position: kept.length + i + 1 }));
  return renumber([...others, ...kept, ...added]);
}

/** Renumérote 1..n par section — même règle que le serveur. */
export function renumber(entries: SupportEntry[]): SupportEntry[] {
  const out: SupportEntry[] = [];
  for (const section of ['arbitrate', 'info'] as Section[]) {
    entries
      .filter((e) => e.section === section)
      .sort((a, b) => a.position - b.position || a.recommendation_id - b.recommendation_id)
      .forEach((e, i) => out.push({ ...e, position: i + 1 }));
  }
  return out;
}

/** Déplace une entrée d'un cran dans sa section. `delta` vaut -1 ou +1.
 *
 *  Les flèches ▲▼ sont l'alternative CLAVIER obligatoire au glisser-déposer :
 *  la page postits a laissé passer ce manque, on ne le reproduit pas.
 */
export function move(entries: SupportEntry[], id: number, delta: number): SupportEntry[] {
  const ordered = renumber(entries);
  const current = ordered.find((e) => e.recommendation_id === id);
  if (!current) return ordered;
  const peers = ordered.filter((e) => e.section === current.section);
  const index = peers.findIndex((e) => e.recommendation_id === id);
  const swapped = peers[index + delta];
  // Un accès hors bornes rend `undefined` — y compris pour un index négatif.
  // C'est exactement le cas « on est déjà en haut ou en bas de la section », et
  // c'est la forme que `noUncheckedIndexedAccess` sait lire : une comparaison
  // de bornes ne restreint pas le type de l'élément lu.
  if (!swapped) return ordered;
  return renumber(
    ordered.map((e) => {
      if (e.recommendation_id === id) return { ...e, position: swapped.position };
      if (e.recommendation_id === swapped.recommendation_id)
        return { ...e, position: current.position };
      return e;
    }),
  );
}

/** Retire une entrée du SUPPORT. Le drapeau, lui, reste posé dans la revue. */
export function remove(entries: SupportEntry[], id: number): SupportEntry[] {
  return renumber(entries.filter((e) => e.recommendation_id !== id));
}

export function add(entries: SupportEntry[], id: number, section: Section): SupportEntry[] {
  if (entries.some((e) => e.recommendation_id === id)) return renumber(entries);
  const last = entries.filter((e) => e.section === section).length;
  return renumber([...entries, { recommendation_id: id, section, position: last + 1 }]);
}

export interface SupportOptions {
  annexe: boolean;
  limites: boolean;
  annotations: boolean;
}

export const DEFAULT_OPTIONS: SupportOptions = {
  annexe: true,
  limites: true,
  annotations: false,
};

/** Les limites ne se décochent pas dès qu'une recommandation `raci` est
 *  présentée : la RACI se lit dans un référentiel dont la revue ne connaît
 *  qu'une partie. */
export function limitsLocked(recos: SupportReco[]): boolean {
  return recos.some((r) => r.family === 'raci');
}

export interface Outline {
  slides: number;
  arbitrate: number;
  info: number;
  families: string[];
  grouped: boolean;
}

/** Le plan, calculé exactement comme le serveur le rendra. */
export function outline(
  entries: SupportEntry[],
  recos: SupportReco[],
  options: SupportOptions,
): Outline {
  const byId = new Map(recos.map((r) => [r.id, r]));
  const rows = renumber(entries)
    .map((e) => ({ entry: e, reco: byId.get(e.recommendation_id) }))
    .filter((r): r is { entry: SupportEntry; reco: SupportReco } => Boolean(r.reco));
  const arbitrate = rows.filter((r) => r.entry.section === 'arbitrate');
  const info = rows.filter((r) => r.entry.section === 'info');
  const families = [...new Set(arbitrate.map((r) => r.reco.family))].sort();
  const grouped = arbitrate.length > DETAIL_MAX;
  const main = grouped ? families.length : arbitrate.length;
  // 2 slides d'ouverture (contexte, constat), puis les sections. Chaque section
  // présente est PRÉCÉDÉE d'un intercalaire (2026-08-26) — deux devant les
  // propositions, un devant « pour information », un devant l'annexe. Ils
  // comptent : l'écran promet un nombre de slides, et le fichier doit le tenir.
  let slides = 2 + (arbitrate.length ? 2 + main + families.length : 0);
  if (info.length) slides += 2;
  if (options.annexe) slides += 1 + arbitrate.length;
  return { slides, arbitrate: arbitrate.length, info: info.length, families, grouped };
}
